import { Component, OnDestroy, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { groupBy, hexToRGB, sortByKey } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ILoopByTrain } from 'src/app/modules/widget/interfaces/loop-by-train';
import { IProductionFront } from 'src/app/modules/widget/interfaces/production-front';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { environment } from 'src/environments/environment';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-loops-by-train',
  templateUrl: './loops-by-train.component.html',
  styleUrls: ['./loops-by-train.component.scss'],
})
export class LoopsByTrainComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public shifts: IProductionFront[][];
  public totalLoops: number;
  public displayMessage = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private langService: LanguageService,
    private datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.shifts = [];
    this.totalLoops = 0;
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  /**
   * Function to draw the chart
   *
   * @param chartOptions ChartOptions
   */
  public drawView(loops: ILoopByTrain[]): void {
    this.displayMessage = !loops.length;
    this.shifts = Object.values(groupBy(loops, (x: ILoopByTrain) => x.shiftName));

    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    loops = sortByKey(loops, 'machineName');

    const totalLoops = loops.map((value) => value.loops || 0);
    this.totalLoops = totalLoops.reduce((a, b) => a + b, 0);

    let categories = loops.map((value) => value.machineName);
    categories = categories.filter((v, i) => categories.indexOf(v) === i);

    const series = this.createSerieData(loops);

    const data = {
      chart: {
        type: 'column',
        zoomType: 'xy',
        backgroundColor: '#F8F8F8',
      },
      title: {
        text: '',
        margin: 50,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories,
      },
      subtitle: {
        text: `${this.langService.message('company')}: ${user.company.name},
              ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
              ${this.langService.message('date')}: ${this.datePipe.transform(date)},
              ${this.langService.message('shift_')}: ${shift.name}`,
      },
      yAxis: {
        min: 0,
        title: {
          text: this.langService.message('loops'),
        },
        stackLabels: {
          enabled: true,
          style: {
            color: '#707070',
            fontWeight: 'bold',
          },
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        backgroundColor: '#433e3e',
        borderColor: '#433e3e',
        borderRadius: 2,
        style: {
          color: 'white',
        },
        useHTML: true,
        formatter(): string {
          const format = `${environment.dateFormatSystem} ${environment.timeFormat}`;
          const lastLoopTranslation = this.langService.message('last_loop');
          const entranceTranslation = this.langService.message('last_transit_in');
          const exitTranslation = this.langService.message('last_transit_out');
          const loopsTranslation = this.langService.message('loops');

          let html = `<div style="width: 130px">
          <strong>${lastLoopTranslation}</strong> <br>
          <span>${this.point.machineName}</span> <br>
          <span>${entranceTranslation}: ${
            this.point.inside !== '0001-01-01T00:00:00+00:00' ? getMoment(this.point.inside, format).format('HH:mm:ss') : '-'
          }</span> <br>
          <span>${exitTranslation}: ${
            this.point.outside !== '0001-01-01T00:00:00+00:00' ? getMoment(this.point.outside, format).format('HH:mm:ss') : '-'
          }</span> <br>`;

          if (this.point.options.isDayDetail) {
            this.point.options.loopsByShift = sortByKey(this.point.options.loopsByShift, 'shiftName');
            for (const shiftDetail of this.point.options.loopsByShift as ILoopByTrain[]) {
              html += `<span>Vueltas T. ${shiftDetail.shiftName}: ${shiftDetail.loops}</span> <br>`;
            }
          } else {
            html += `<span>${loopsTranslation}: ${this.y}</span> <br>`;
          }

          html += `</div>`;

          return html;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series,
    };
    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      /* if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer.text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px'
          })
          .add();
      } */
    });
  }

  /**
   * Function to display the legend box
   *
   * @param shift Current shift
   */
  public styleShiftLegend(shift: IProductionFront): any {
    return {
      'background-color': hexToRGB(shift.shiftColor, '0.5'),
      border: `2px solid ${shift.shiftColor}`,
    };
  }

  /**
   * Function to create a serie data for chart
   */
  private createSerieData(loops: ILoopByTrain[]): any {
    let shifts = loops.map((value) => value.shiftName);
    shifts = shifts.filter((v, i) => shifts.indexOf(v) === i);
    shifts.sort();
    shifts.reverse();

    const series = [];
    for (const shift of shifts) {
      // Buscar si existe algún dato para esa máquina
      const machineData = loops.filter((value) => value.shiftName === shift);

      for (const machine of machineData) {
        const found = series.find((value) => value.name === machine.shiftName);

        if (found) {
          found.data.push({
            machineName: machine.machineName,
            inside: machine.lastInTransit,
            outside: machine.lastOutTransit,
            loopsByShift: loops.filter((value) => value.machineId === machine.machineId),
            isDayDetail: shifts.length > 1,
            y: machine.loops,
          });
        } else {
          series.push({
            name: machine.shiftName,
            color: hexToRGB(machine.shiftColor, '0.5') || hexToRGB('#707070', '0.5'),
            borderColor: machine.shiftColor || '#707070',
            data: [
              {
                machineName: machine.machineName,
                inside: machine.lastInTransit,
                outside: machine.lastOutTransit,
                loopsByShift: loops.filter((value) => value.machineId === machine.machineId),
                isDayDetail: shifts.length > 1,
                y: machine.loops,
              },
            ],
          });
        }
      }
    }

    return series;
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
